/**
 * Copyright (c) Qwoach LLC 2020. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Artem Bogush <artem@qwoach.com> on 7/26/19
 *
 */

import {FirestoreDB} from "@qwoach/firebase-data-manager";
import DataFilter from "@/core/js/backend/filters/BaseDataFilter.js";
import OldFunnelItem from "@/core/modules/coach/Clients/Funnel/model/old/OldFunnelItem";
import NoteItem from "@/core/js/model/Profile/NoteItem";
import {plainToClass, classToPlain} from "@qwoach/class-transformer-wrapper"
import UserProfile from "@/core/js/model/Profile/UserProfile";
import AssessmentItem from "@shared/Assessment/AssessmentItem";
import EventType, {EventTypeCategory} from "@shared/scheduler/EventType";
import ScheduledEvent from "@shared/scheduler/ScheduledEvent";
import CoachingProgram, {ProgramCategoryEnum} from "@shared/Program/CoachingProgram";
import BillingData from "@shared/Billing/BillingData";
import TagsContainer from "@shared/Tags/TagsContainer";
import ClientProfile from "@shared/Profile/ClientProfile";
import DocSignTemplateItem from "@/core/modules/DocSign/data/DocSignTemplateItem";
import SignatureItem from "@/core/modules/DocSign/data/SignatureStore/SignatureItem";
import DocSignDocument from "@/core/modules/DocSign/data/DocSignDocument/DocSignDocument";
import {BusyTimeResponse} from "@shared/scheduler/BusyTimeResponse";
import PaymentIntegrations from "@shared/Billing/PaymentIntegrations";
import InvoiceGroup from "@shared/Invoice/InvoiceGroup";
import TimeBasedItem from "@shared/DueDatedItem/TimeBasedItem";
import ProgramInvite from "@shared/Program/ProgramInvite";
import CalendarEventResponse from "@shared/scheduler/CalendarEventResponse";
import {SessionConfirmationStatus} from "@shared/Program/ProgramStep/helpers/ProgramStepEnums";
import QwoachFile from "@shared/Drive/QwoachFile";
import UsageStats from "@shared/UsageStats";
import UserSubscription from "@shared/Subscription/UserSubscription";
import FormItem from "@/core/modules/coach/Resources/Forms/model/FormItem";
import FormAnswerItem from "@/core/modules/coach/Resources/Forms/model/FormAnswerItem";
import FunnelEntryItem from "@/core/modules/coach/Clients/Funnel/model/old/FunnelEntryItem";
import DatabaseSettingsItem from "@/utils/dataMigration/database/DatabaseSettingsItem";
import {SortDirection} from "@/core/js/backend/filters/SortDirection";
import ErrorStackParser from "error-stack-parser"
import {env, getApiDomain, isDevEnv, isProdEnv} from "@/utils/helpers";
import ProgramInviteInfo from "@shared/Program/ProgramInviteInfo";
import RecurringBillingItem from "@shared/Billing/RecurringBillingItem";
import {JournalEntry} from "@shared/Journal/journalEntry-imports.js";
import {DisplayDestinationEnum} from "@shared/Journal/DisplayDestinationEnum";
import GenogramItem from "@shared/Genogram/GenogramItem";
import CalendarIntegrations from "@shared/integrations/calendar/CalendarIntegrations";
import TimelineItem from "@shared/Timeline/TimelineItem";
import {WorkflowItem} from "@shared/Workflow/WorkflowItem-imports.js";
import TraumaWorkflowDocumentsWrapper from "@shared/Workflow/TraumaWorkflowDocumentsWrapper";
import {TraumaCoachingWorkflowItem} from "@shared/Workflow/WorkflowItem-imports";
import {WorkflowStateItem} from "@shared/Workflow/WorkflowStateItem-imports";
import CoachingPackageTemplate from "@shared/CoachingPackage/CoachingPackageTemplate";
import CoachingPackageInfo from "@shared/CoachingPackage/CoachingPackageInfo";
import PurchasedCoachingPackage from "@shared/CoachingPackage/PurchasedCoachingPackage";
import InvoiceItem from "@shared/Invoice/InvoiceItem";
import LinkItem from "@shared/Drive/LinkItem";
import TimeZoneItem from "@shared/Profile/TimeZoneItem";
import WheelItem from "@shared/WheelModel/WheelItem";
import WheelAnswerItem from "@shared/WheelModel/WheelAnswerItem";
import CompanyIndexItem from "@shared/Company/CompanyIndexItem";
import dayjs from "dayjs";
import TeamMemberProfile from "@shared/Profile/TeamMemberProfile.js";
import IFSPartsItem from "@shared/Genogram/IFSPartsItem.js";
import {WORKFLOW_ID_PROSPECTING} from "@shared/Workflow/base/WorkflowItem";
import WorkflowEntryItem from "@shared/Workflow/base/WorkflowEntryItem";
import ReminderItem from "@shared/scheduler/reminders/ReminderItem";
import WorkflowProspectResponse from "@shared/Workflow/WorkflowProspectResponse";
import PlanSwitchPreview from "@shared/Subscription/PlanSwitchPreview";
import QwoachFileResponse from "@shared/Drive/QwoachFileResponse";
import UISubscription from "@shared/Subscription/UISubscription";


const fbPathsConfig = {

	// ================================= firestore db ======================================================================

	getDatabaseSettings:    	["users/{this.currentUserId}/settings/--databaseSettings--", DatabaseSettingsItem],

	getUserProfile:    			["users/{userId}", UserProfile],
	getCurrentUserProfile:  	["users/{this.currentUserId}", UserProfile],
	getUserSubscription: 		["users/{userId}/subscription/--subscription--", UserSubscription],
	onSnapshotUserSubscription:	["users/{userId}/subscription/--subscription--", UserSubscription],

	onSnapshotCustomClaimsDoc:  ["users/{this.currentUserId}/utils/--customClaimsChangeDoc--"],


	getLinkItem: 				["users/{this.ownerId}/resources/links/linkItems/{linkId}", LinkItem],

	getDriveFile: 				["users/{this.ownerId}/resources/drive/driveFiles/{fileId}", QwoachFile, {permissions: ['resource_scope_all']}],
	batchSetDriveFiles:			["users/{this.ownerId}/resources/drive/driveFiles"],
	batchUpdateDriveFiles:		["users/{this.ownerId}/resources/drive/driveFiles"],

	getDriveStatsOld: 			["users/{this.ownerId}/resources/drive/settings/--driveStats--"],

	getUsageStats: 				["users/{this.ownerId}/settings/--usageStats--", UsageStats, {write: false}], //only generate get method, but not update, delete etc. as these stats only writable on server since they control critical user access features.
	onSnapshotUsageStats: 		["users/{this.ownerId}/settings/--usageStats--", UsageStats],


	getFormItem: 				["users/{this.ownerId}/resources/forms/formQuestions/{formId}", FormItem],
	getFormAnswerItem: 			["users/{this.ownerId}/resources/forms/formAnswers/{formAnswerId}", FormAnswerItem, {permissions: ['form_completed_scope_all'], requiresClearance: true}], //requiresClearance value must be in sync with the security rules
	batchDeleteFormAnswerItems:	["users/{this.ownerId}/resources/forms/formAnswers", {requiresClearance: true}],

	getWorkflowItem: 			["users/{this.ownerId}/resources/workflows/workflowsCollection/{workflowId}", WorkflowItem],
	getWorkflowEntryItem: 		["users/{this.ownerId}/resources/workflows/workflowsEntries/{workflowEntryItemId}", WorkflowEntryItem],
	batchUpdateWorkflowEntryItem: ["users/{this.ownerId}/resources/workflows/workflowsEntries", WorkflowEntryItem],

	getWorkflowStateItem: 		["users/{this.ownerId}/resources/workflows/workflowsCollection/{?workflowId}/workflowStateItems/{workflowStateItemId}", WorkflowStateItem, {requiresClearance: true}], //{?workflowId} - '?' means dataManager will try to locate that parameter in properties of the provided WorkflowStateItem object in which case it can be amended from parameters
	batchUpdateWorkflowStateItem:["users/{this.ownerId}/resources/workflows/workflowsCollection/{?workflowId}/workflowStateItems", WorkflowStateItem, {requiresClearance: true}], //{?workflowId} - '?' means dataManager will try to locate that parameter in properties of the provided WorkflowStateItem object in which case it can be amended from parameters

	getClientProfile: 			["users/{this.ownerId}/clients/{clientId}", ClientProfile, {permissions: ['client_scope_all'], requiresClearance: false}], //requiresClearance value must be in sync with the security rules
	getClientProfileCount: 		["users/{this.ownerId}/clients"],
	// getClientProfileSchemaVersionSum: 		["users/{this.ownerId}/clients:schemaVersion"],
	// getClientProfileSchemaVersionAverage: 	["users/{this.ownerId}/clients", {aggregateBy: "schemaVersion"}],
	batchUpdateClientProfile: 	["users/{this.ownerId}/clients", ClientProfile],

	getTeamMemberProfile: 		 ["users/{this.ownerId}/team/--teamDoc--/members/{memberId}", TeamMemberProfile],
	batchUpdateTeamMemberProfile:["users/{this.ownerId}/team/--teamDoc--/members", TeamMemberProfile],

	getJournalEntry: 			["users/{this.ownerId}/journalEntries/{journalEntryId}", JournalEntry, {permissions: ['journal_scope_all'], requiresClearance: true}], //requiresClearance value must be in sync with the security rules
	batchUpdateJournalEntry:	["users/{this.ownerId}/journalEntries", JournalEntry, {requiresClearance: true}], //requiresClearance value must be in sync with the security rules

	getProgram: 				["users/{this.ownerId}/programs/{programId}", CoachingProgram],
	onSnapshotProgram: 			["users/{this.ownerId}/programs/{programId}", CoachingProgram],
	getProgramGroupRef: 		["programs", CoachingProgram],

	getProgramInvite: 			["users/{this.ownerId}/programInvites/{programInviteId}", ProgramInvite],
	getProgramInvitePublic: 	["users/{coachId}/programInvites/{programInviteId}", ProgramInvite],


	getCoachingPackageTemplate:				["users/{this.ownerId}/coachingPackages/--coachingPackagesDoc--/templates/{templateId}", CoachingPackageTemplate],
	getPurchasedCoachingPackage:			["users/{this.ownerId}/coachingPackages/--coachingPackagesDoc--/purchasedPackages/{packageId}", PurchasedCoachingPackage],
	batchUpdatePurchasedCoachingPackage:	["users/{this.ownerId}/coachingPackages/--coachingPackagesDoc--/purchasedPackages", PurchasedCoachingPackage],


	getFunnel:    				["users/{this.ownerId}/funnels/{funnelId}", OldFunnelItem],
	getFunnelMain:    			["users/{this.ownerId}/funnels/--mainFunnel--", OldFunnelItem],
	getFunnelEntry:    			["users/{this.ownerId}/funnels/{funnelId}/funnelEntries/{funnelEntryId}", FunnelEntryItem],
	batchSetFunnelEntry:		["users/{this.ownerId}/funnels/{funnelId}/funnelEntries", FunnelEntryItem],

	getNote:    				["users/{this.ownerId}/notes/{noteId}", NoteItem],
	batchSetNotes:				["users/{this.ownerId}/notes", NoteItem],

	getTagsContainer:   		["users/{this.ownerId}/tagsContainer/{containerId}", TagsContainer],

	getDocSignTemplate:    		["users/{this.ownerId}/docSignTemplates/{docSignTemplateId}", DocSignTemplateItem],

	getContract:    			["users/{this.ownerId}/contracts/{docSignDocumentId}", DocSignDocument, {requiresClearance: true}], //requiresClearance value must be in sync with the security rules
	batchUpdateContract:		["users/{this.ownerId}/contracts", DocSignDocument, {requiresClearance: true}],						//requiresClearance value must be in sync with the security rules
	onSnapshotContract: 		["users/{this.ownerId}/contracts/{docSignDocumentId}", DocSignDocument, {requiresClearance: true}], //requiresClearance value must be in sync with the security rules

	getSignatureItem:    		["users/{this.currentUserId}/signatures/{signatureId}", SignatureItem],

	getBillingData:    			["users/{this.ownerId}/billingData/{billingDataId}", BillingData],

	//only for coach, client will get it from the api stripped for security
	getPaymentIntegrations: 	["users/{this.ownerId}/paymentIntegrations/mainDoc", PaymentIntegrations],

	getInvoiceGroupItem: 		["users/{this.ownerId}/invoices/{invoiceGroupId}", InvoiceGroup],
	getInvoiceItem: 			["users/{this.ownerId}/invoices/{invoiceId}", InvoiceItem],
	onSnapshotInvoiceItem: 		["users/{this.ownerId}/invoices/{invoiceId}", InvoiceItem],

	getTimeBasedItem: 			["users/{this.ownerId}/timeBasedItems/{timeBasedItemId}", TimeBasedItem],


	//scheduler
	getEventType:    			["users/{this.ownerId}/eventTypes/{eventTypeId}", EventType, {get: false, permissions: ['scheduler_scope_all']}], //don't generate get method. use apiGetEventType instead since it's a complex object constructed from multiple
	getScheduledEventItem:  	["users/{this.ownerId}/events/{scheduledEventId}", ScheduledEvent],
	batchUpdateEventType:		["users/{this.ownerId}/eventTypes", EventType],

	getEventTypeMe:    			["users/{this.currentUserId}/eventTypes/{eventTypeId}", EventType, {get: false}], //don't generate get method. use apiGetEventType instead since it's a complex object constructed from multiple

	//public paths
	getAssessment: 				["users/{userId}/assessments/{assessmentId}", AssessmentItem],
	getAssessmentPublic: 		["users/{userId}/assessmentsPublic/{assessmentId}", AssessmentItem],


	//genogram
	getGenogramItem: 			["users/{this.ownerId}/genograms/{genogramId}", GenogramItem, {requiresClearance: false}], //requiresClearance value must be in sync with the security rules
	batchUpdateGenogramItem: 	["users/{this.ownerId}/genograms", GenogramItem, {requiresClearance: false}], //requiresClearance value must be in sync with the security rules

	getIFSPartsItem: 			["users/{this.ownerId}/ifsparts/{partsId}", IFSPartsItem, {requiresClearance: false}], //requiresClearance value must be in sync with the security rules

	getWheelOfLifeTemplate: 	["users/{this.ownerId}/wheelOfLife/-/wheelOfLifeTemplates/{wheelOfLifeId}", WheelItem],
	getWheelOfLifeResponse: 	["users/{this.ownerId}/wheelOfLife/-/wheelOfLifeResponses/{wheelOfLifeId}", WheelAnswerItem, {requiresClearance: false}], //requiresClearance value must be in sync with the security rules
	batchSetWheelOfLifeResponse:["users/{this.ownerId}/wheelOfLife/-/wheelOfLifeResponses", WheelAnswerItem, {requiresClearance: false}], //requiresClearance value must be in sync with the security rules

	getCompanyIndex: 			["users/{this.ownerId}/containers/--companyContainer--", CompanyIndexItem],

	//Timeline
	getTimelineItem: 			["users/{this.ownerId}/traumaTimelines/{timelineId}", TimelineItem, {requiresClearance: false}],
	batchUpdateTimelineItem: 	["users/{this.ownerId}/traumaTimelines", TimelineItem, {requiresClearance: false}],




	// ================================= api ======================================================================

	// apiPostTestAnalytics: ["api/public/test/analytics"],


	//billing



	apiGetEventType: 							["api/model/sessionType", EventType],
	apiGetEventTypeArray: 						["api/model/sessionTypeArray", EventType],


	apiPostTest: 								["api/test"],

	//user
	apiGetIsUserSame: 							["api/public/user/isSame"],
	apiPostCreateCoachProfileSpecialFreeTrial: 	["api/public/user/create/coach/free/trial", UserProfile],
	apiPostCreateCoachProfileFreeAccount: 		["api/public/user/create/coach/free/account", UserProfile],
	apiPostTeacherAccountFormSubmitted: 		["api/user/teacher/account/form/submitted"],
	apiPostTeacherAccountCreated: 				["api/user/teacher/account/ready"],
	apiPostCreateStudentAccountLocked:			["api/public/user/create/student/locked", UserProfile],
	apiPostCreateCoachProfile: 					["api/user/create/coach", UserProfile],
	apiPostCreateClient: 	   					["api/public/user/create/client", UserProfile],
	apiPostCreateTeamMemberAccount:				["api/public/user/create/member", UserProfile],
	apiGetUserProfilePublic: 					["api/public/user/get", UserProfile],
	apiPostZip: 								["api/utils/zip"],
	apiGetCoachProfileByEvenTypeUserId: 		["api/public/user/byEventTypeUserId", UserProfile],
	apiGetCoachProfileById: 					["api/public/user/byId", UserProfile],
	apiPostSetTeamMemberEnabled: 				["api/team/member/enabled/set", TeamMemberProfile],
	apiGetCoachesTeamListPublic:				["api/public/team/member/list/coaches", TeamMemberProfile],
	apiGetUserProfileByMemberProfile:			["api/public/team/member/userprofile", UserProfile],
	apiPostCreatePendingTeamMember:				["api/user/member/create/pending", TeamMemberProfile],
	apiPostDeleteTeamMemberProfile: 	   		["api/team/delete/member/profile", UserProfile],
	apiGetCurrentCoachProfile: 					["api/user/profile/coach", UserProfile],
	apiPostSetUserTimezone: 					["api/user/timezone/set"],

	apiPostBatchUpdateTeamMemberProfile:		["api/team/member/batch/update", TeamMemberProfile],
	apiPostTeamSeatsNumberChanged:				["api/team/seats/changed"],

	apiPostCreateGenogramAndClientItem: 		["api/public/client/genogram/create", GenogramItem],




	//client
	apiPostEnsureClientProfileExists: 			["api/client/profile/ensureExists", ClientProfile],
	apiPostCreateClientProfile: 				["api/public/client/profile", ClientProfile],
	// apiGetClientProfile: 					["api/public/client/profile", ClientProfile],
	apiPostSetClientEnabled: 					["api/client/enabled/set", ClientProfile],
	apiGetPublicClientProfileMinimal:			["api/public/client/profile", ClientProfile],
	apiPostRecalculateClientProfilesCount: 		["api/client/profile/count/recalculate"],
	apiPostConvertProspectToClient: 			["api/client/convert/from/prospect/", ClientProfile],
	apiPostDeleteProspect: 						["api/client/delete/prospect"],

	//assessment
	apiGetAssessmentPublic: 					["api/assessment/public/get", AssessmentItem],
	apiPostAssessmentUpdateAssignee: 			["api/assessment/public/updateAssignee", AssessmentItem],
	apiPostAssessmentNotifyCompleted: 			["api/assessment/public/notifyCompleted"],
	apiPostAssessmentSendFinalReport: 			["api/assessment/public/sendFinalReport", AssessmentItem],

	//scheduler
	apiGetSchedulerFindUserByEventTypeUserId: 	["api/scheduler/eventTypeUser"],

	apiGetEventTypePublic: 						["api/scheduler/public/eventType", EventType],
	apiGetEventTypeByIdPublic: 					["api/scheduler/public/eventTypeById", EventType],
	apiGetScheduledEventPublic: 				["api/scheduler/public/scheduledEvent", ScheduledEvent],
	apiGetSchedulerPublicGetEventById: 			["api/calendar/public/event", ScheduledEvent],


	apiPostEventCreatedSendEmail: 				["api/calendar/public/eventCreate/notify", ScheduledEvent],
	apiPostEventCreate: 						["api/calendar/public/eventCreate", ScheduledEvent],
	apiPostEventCreatePending: 					["api/calendar/public/eventCreate/pending", ScheduledEvent],
	apiPostEventCreatePendingMultiple: 			["api/public/calendar/eventCreate/pending/multiple"],

	apiPostEventRescheduled: 					["api/calendar/public/event/reschedule", ScheduledEvent],
	apiPostEventCancel: 						["api/calendar/public/event/cancel", ScheduledEvent],
	apiPostEventSyncDelete: 					["api/calendar/event/sync/delete", ScheduledEvent],
	// apiPostEventSyncUpdateMultiple:			["api/calendar/event/sync/updateMultiple", ScheduledEvent],
	apiPostEventCancelNotify: 					["api/calendar/public/event/cancel/notify"],

	//use apiPostScheduledEvent* apis below as they also syncs reminders
	apiPostScheduledEventCreate: 				["api/scheduledEvent/create", ScheduledEvent],
	apiPostScheduledEventCreateMultiple: 		["api/scheduledEvent/createMultiple", ScheduledEvent],
	apiPostScheduledEventUpdateMultiple: 		["api/scheduledEvent/updateMultiple", ScheduledEvent],
	// apiPostScheduledEventUpdate:				["api/scheduledEvent/update", ScheduledEvent],
	apiPostScheduledEventDelete: 				["api/scheduledEvent/delete"],
	apiPostScheduledEventDeleteMultiple: 		["api/scheduledEvent/deleteMultiple", ScheduledEvent],

	apiPostBusyTimePeriods: 					["api/calendar/public/busy", BusyTimeResponse],
	apiPostCheckAvailabilityMultiple: 			["api/public/calendar/availability/check/multiple"],

	apiPostCopyEventType: 						["api/scheduler/eventType/copy", EventType],
	apiPostDeleteEventType: 					["api/scheduler/eventType/delete", EventType],


	//calendar
	apiGetCalendarIntegrations: 				["api/calendar/integrations", CalendarIntegrations],
	apiPostCalendarIntegrations: 				["api/calendar/integrations", CalendarIntegrations],
	apiPostCalendarIntegrationsUpdateCalendarItem: ["api/calendar/integrations/calendarItem", CalendarIntegrations],
	apiPostIntegrationCalendarItemDelete: 		["api/calendar/integrations/calendarItem/delete", CalendarIntegrations],

	apiPostGCalendarAuthStart: 					["api/calendar/auth"],
	apiPostOutlook365AuthStart: 				["api/calendar/outlook365/auth"],

	apiGetCalendarEventRecurringInstances: 		["api/calendar/event/instances", CalendarEventResponse],
	apiPostCalendarEventUpdate: 				["api/calendar/event/update"],
	apiPostCalendarEventUpdateFuture: 			["api/calendar/event/updateFuture", CalendarEventResponse],
	apiPostCalendarEventDelete: 				["api/calendar/event/delete"],
	apiPostCalendarEventDeleteFuture: 			["api/calendar/event/deleteFuture", CalendarEventResponse],
	apiPostCalendarEnableWatchers: 				["api/calendar/event/enableWatchers/all"],


	//workflow
	apiGetTraumaWorkflowDefaultDocuments: 		["api/workflow/documents/default/traumaworkflow/all", TraumaWorkflowDocumentsWrapper],
	apiPostTraumaWorkflowSetupDocuments: 		["api/workflow/documents/traumaworkflow/setup", TraumaCoachingWorkflowItem],
	apiPostTraumaWorkflowUpdateSessionTriggers: ["api/workflow/documents/traumaworkflow/triggers/session/update", TraumaCoachingWorkflowItem],
	apiPostTraumaWorkflowEnter: 				["api/workflow/documents/traumaworkflow/enter"],

	apiPostWorkflowEntryProspectCreate: 		["api/workflow/prospect/create", WorkflowProspectResponse],
	apiPostWorkflowEntryProspectDelete: 		["api/workflow/prospect/delete", WorkflowProspectResponse],
	apiPostWorkflowItemProspectCreate: 			["api/workflow/create", WorkflowItem],

	//reminder
	apiPostAddReminderItem: 					["api/reminder/create", ReminderItem],
	apiPostAddProspectReminder: 				["api/reminder/prospect/create", WorkflowEntryItem],
	apiPostUpdateProspectReminder: 				["api/reminder/prospect/update", WorkflowEntryItem],
	apiPostDeleteProspectReminder: 				["api/reminder/prospect/delete", WorkflowEntryItem],

	//general notifications
	apiPostSendNotificationEmail: 				["api/notification/email/send"],
	apiPostSendNotificationEmailMultiple: 		["api/notification/email/send/multiple"],

	//invite
	apiPostAcceptProgramInvite: 				["api/program/invite/accept"],
	apiPostSendInvite: 							["api/program/invite/send"],
	apiPostResendInvite: 						["api/program/invite/resend"],
	apiGetProgramInviteInfo: 					["api/public/program/invite/info", ProgramInviteInfo],

	apiPostAcceptPortalInvite: 					["api/invite/portal/accept"],
	apiPostSendPortalInvite: 					["api/invite/portal/send"],
	apiPostPortalInviteCreate: 					["api/invite/portal/create"],
	apiGetPortalInviteInfo: 					["api/public/invite/portal/info", ProgramInviteInfo],

	apiPostAcceptTeamInvite: 					["api/team/invite/accept"],
	apiPostSendTeamInvite: 						["api/team/invite/send"],
	apiGetTeamInviteInfo: 						["api/public/team/invite/info", ProgramInviteInfo],

	//program
	apiPostProgramDelete: 						["api/program/delete"],
	apiPostProgramCopy: 						["api/program/copy", CoachingProgram],
	apiPostStartCoachingProcess: 				["api/program/coaching/start", CoachingProgram],
	apiPostRemoveClientFromProgram: 			["api/program/client/remove"],

	// apiPostSendCoachingPackageInvite: 		["api/coachingPackage/invite/send"],
	apiGetCoachingPackageCheckoutInfo: 			["api/public/coachingPackage/checkout/info", CoachingPackageInfo],
	apiGetGroupPackageCheckoutInfo: 			["api/public/coachingPackage/checkout/group/info", CoachingPackageInfo],
	// apiGetCoachingPackageInviteInfo: 		["api/public/coachingPackage/invite/info", ProgramInviteInfo],
	// apiPostAcceptCoachingPackageInvite: 		["api/coachingPackage/invite/info"],

	apiPostStartGroup: 							["api/coachingPackage/group/start", PurchasedCoachingPackage],
	apiPostDeleteGroup: 						["api/coachingPackage/group/delete"],
	apiPostGroupShare:							["api/coachingPackage/group/share"],
	apiPostGroupUnshare:						["api/coachingPackage/group/unshare"],
	apiPostGroupAddSessions: 					["api/coachingPackage/group/sessions/add", PurchasedCoachingPackage],



	//utils
	apiPostLogError:							["api/public/utils/logError"],
	apiPostLogInfo:								["api/public/utils/logInfo"],
	apiGetEnchargeUniqueFields: 				["api/public/utils/enchargePerson/uniqueprops"],
	apiGetClientTimezoneFromAddress: 			["api/utils/timezone/fromaddress", TimeZoneItem],


	//survey
	apiPostNotifySurveySubmitted: 				["api/public/notification/email/survey/submitted"],
	apiPostFeedbackSubmitted: 					["api/public/notification/email/feedback/submitted"],


	//pdf
	apiPostPdfToImage: 							["api/pdf/to/image"],
	apiPostHtmlToPdf: 							["api/public/pdf/from/html"],
	// apiPostHtmlToPdf: ["api/assessment/public/generateReportPdf"],

	apiPostCreateContractTemplate: 				["api/contract/template/create", DocSignTemplateItem],
	apiPostDeleteContractTemplate: 				["api/contract/template/delete"],
	apiPostDuplicateContractTemplate: 			["api/contract/template/duplicate", DocSignTemplateItem],
	apiPostRenameContractTemplate: 				["api/contract/template/rename", DocSignTemplateItem],
	apiPostSendTemplateForSignature: 			["api/contract/template/send"],
	apiPostResendContractNotification: 			["api/contract/resend"],



	apiPostSetContract: 						["api/contract/create", DocSignDocument],
	apiPostDeleteContract: 						["api/contract/delete"],
	apiPostContractSignedPublic: 				["api/public/contract/signed", DocSignDocument],
	apiGetContractPublic: 						["api/public/contract", DocSignDocument],

	apiPostCreateSignatureItem: 				["api/contract/signature/create", SignatureItem],
	apiPostDeleteSignatureItem: 				["api/contract/signature/delete"],


	//user subscription
	apiGetPlanItemPublic: 						["api/public/paddle/plan", UserSubscription],
	apiPostQwoachCancelSubscription: 			["api/paddle/subscription/cancel"],
	apiPostValidateWhiteLabelingIntegration: 	["api/branding/whitelabeling/validate"],
	apiGetPreviewSubscriptionUpdate: 			["api/paddle/subscription/update/preview", PlanSwitchPreview],
	apiPostSubscriptionUpdate: 					["api/paddle/subscription/update"],
	apiGetSubscriptionsForUI: 					["api/billing/subscription/all", UISubscription],
	apiGetUserSubscription: 					["api/user/subscription", UserSubscription],



	//payment integration
	apiGetHasPaymentIntegrations: 				["api/paymentIntegration/hasIntegrations"],

	apiPostPayPalIntegrationValidate: 			["api/paymentIntegration/paypal/validate"],
	apiPostPayPalPaymentLink: 					["api/public/paymentIntegration/paypal/generatePaymentLink"],

	apiPostPayPalCreateOrder: 					["api/public/paymentIntegration/paypal/createOrder"],
	apiPostPayPalApproveOrder: 					["api/public/paymentIntegration/paypal/approveOrder"],

	apiGetStripeAuthUrl: 						["api/paymentIntegration/stripe/authUrl"],
	apiGetStripeApiKey: 						["api/public/paymentIntegration/stripe/apiKey"],
	apiPostStripeCreateOrder: 					["api/public/paymentIntegration/stripe/order"],
	apiGetConnectedBillingAccounts: 			["api/public/paymentIntegration/connectedAccounts"],
	apiPostStripeCreateSubscription: 			["api/paymentIntegration/stripe/subscription"],
	apiPostStripeCancelSubscription: 			["api/paymentIntegration/stripe/subscription/cancel"],
	apiPostStripeUpdateRecurringPricing: 		["api/paymentIntegration/stripe/subscription/pricing/update", RecurringBillingItem],
	apiPostConfirmFreeOrder: 					["api/public/paymentIntegration/free/join", InvoiceItem],

	apiGetCloverOAuthStart: 					["api/paymentIntegration/clover/oauth/start"],
	apiPostCloverOrderStart: 					["api/public/paymentIntegration/clover/order"],

	//zoom integration

	apiGetZoomAuthUrl: 							["api/integrations/zoom/oauth/start"],

	//invoices
	apiPostInvoiceGroupSendEmailNotification: 	["api/invoice/notification/askPay"],
	apiPostInvoiceGroupPaidSendEmailNotification: ["api/public/invoice/notification/paymentSent"],
	apiGetInvoiceGroupItem: 					["api/public/invoice/get", InvoiceGroup],
	apiGetInvoiceItem: 							["api/public/invoice/get", InvoiceItem],
	apiPostUpdateInvoiceGroupItem: 				["api/public/invoice/update", InvoiceGroup],
	apiPostCreateTempInvoice: 					["api/invoice/create/temp", InvoiceGroup],
	apiPostRemoveTempInvoice:					["api/invoice/remove/temp"],


	// wheel-of-life
	apiGetWheelItemPublic: 						["api/public/resources/wheel", WheelItem],
	apiGetWheelItemResponsePublic: 				["api/public/resources/wheel/response", WheelAnswerItem],
	apiPostWheelAnswersSubmit: 					["api/public/resources/wheel/submit", FormAnswerItem],
	apiPostExportWOLResponses: 					["api/resources/wheel/responses/export"],

	// forms
	apiGetFormItemPublic: 						["api/resources/forms/public/get", FormItem],
	apiPostFormAnswersSubmit: 					["api/resources/forms/public/submit", FormAnswerItem],
	apiPostShareFormWithClient:  				["api/resources/forms/share/client"],

	//payment
	apiPostGeneratePayLink: 					["api/public/paddle/generatepaylink"],

	//storage

	apiGetStorageFileLink: 						["api/public/storage/link"],
	apiGetStorageFileStream: 					["api/public/storage/stream"],

	apiPostShareFilesWith: 						["api/drive/share", QwoachFileResponse],
	apiPostUnshareFilesWith: 					["api/drive/unshare", QwoachFileResponse],

	//migration

	apiPostMigrateDriveStatsToUsageStats: 		["api/migration/driveStats/usageStats"],


	apiPostTestAmplitudeEventPropsMerge: 		["api/public/utils/amplitude/eventPropsMerge"],

}


/**
 *
 * @property { function() } generateId
 *
 * @property {function(methodName)} getRawPathForMethodName
 *
 *
 * @property { function() } fieldValueDelete
 * @property { function() } fieldValueArrayRemove
 * @property { function() } fieldValueArrayUnion
 * @property { function() } fieldValueIncrement
 * @property { function() } fieldValueServerTimestamp
 *
 * @property { function(userId) } getUserProfile
 * @property { function(userItem) } updateUserProfile
 * @property { function(userItem) } setUserProfile
 * @property { function(userItem) } createUserProfile
 * @property { function(userItem) } deleteUserProfile
 *
 * @property { function(userId) } getUserSubscription
 *
 *
 * @property { function(userId) }   getDatabaseSettings
 * @property { function(userId) }   setDatabaseSettings
 * @property { function(userId) }   updateDatabaseSettings

 *
 * @property { function() } getClientProfileList
 * @property { function() } getClientProfileListPager
 * @property { function() } getClientProfileListRef
 * @property { function(clientProfileId) } getClientProfile
 * @property { function(clientProfileItem) } updateClientProfile
 * @property { function(clientProfileItem) } setClientProfile
 * @property { function(clientProfileItem) } createClientProfile
 * @property { function(clientProfileItem) } deleteClientProfile
 *
 *
 * @property { function() } getTeamMemberProfileList
 * @property { function() } getTeamMemberProfileListPager
 * @property { function() } getTeamMemberProfileListRef
 * @property { function(profileId) } getTeamMemberProfile
 * @property { function(profileId) } updateTeamMemberProfile
 * @property { function(profileId) } setTeamMemberProfile
 * @property { function(profileId) } createTeamMemberProfile
 * @property { function(profileId) } deleteTeamMemberProfile
 *
 *
 * @property { function() } getCurrentUserProfile
 *
 *
 * @property { function() } getJournalEntryList
 * @property { function() } getJournalEntryListPager
 * @property { function() } getJournalEntryListRef
 * @property { function(journalEntryId) } getJournalEntry
 * @property { function(journalEntry) } updateJournalEntry
 * @property { function(journalEntry) } setJournalEntry
 * @property { function(journalEntry) } createJournalEntry
 * @property { function(journalEntry) } deleteJournalEntry
 *
 *
 * @property { function() } getProgramList
 * @property { function() } getProgramGroupRef
 *
 * @property { function(programId) }   getProgram
 * @property { function(programItem) } updateProgram
 * @property { function(programItem) } createProgram
 * @property { function(programItem) } deleteProgram
 *
 *
 * @property { function(programInviteId) }   getProgramInviteListRef
 * @property { function(programInviteId) }   getProgramInvite
 * @property { function(programInviteItem) } updateProgramInvite
 * @property { function(programInviteItem) } createProgramInvite
 * @property { function(programInviteItem) } deleteProgramInvite
 *
 *
 *
 *
 * @property { function() } getFunnelListRef
 *
 * @property { function(funnelItem) } setFunnelMain
 *
 *
 * @property { function() } getCoachingPackageTemplateListPager
 * @property { function() } getCoachingPackageTemplateListRef
 * @property { function(coachingPackageTemplate) } setCoachingPackageTemplate
 * @property { function(coachingPackageTemplate) } updateCoachingPackageTemplate
 * @property { function(coachingPackageTemplate) } deleteCoachingPackageTemplate
 *
 * @property { function() } getPurchasedCoachingPackageListPager
 * @property { function() } getPurchasedCoachingPackageListRef
 * @property { function(purchasedCoachingPackage) } setPurchasedCoachingPackage
 * @property { function(purchasedCoachingPackage) } updatePurchasedCoachingPackage
 * @property { function(purchasedCoachingPackage) } deletePurchasedCoachingPackage
 *
 *
 *
 *
 * @property { function() } getFunnel
 * @property { function(funnelItem) } updateFunnel
 * @property { function(funnelItem) } setFunnel
 * @property { function(funnelItem) } createFunnel
 * @property { function(funnelItem) } deleteFunnel
 *
 * @property { function(funnelId) } getFunnelEntryListRef
 * @property { function(funnelId, funnelEntryId) } getFunnelEntry
 * @property { function(funnelEntryItem) } updateFunnelEntry
 * @property { function(funnelEntryItem) } setFunnelEntry
 * @property { function(funnelEntryItem) } createFunnelEntry
 * @property { function(funnelEntryItem) } deleteFunnelEntry
 * @property { function(key, keyValue) } deleteAllFunnelEntry
 *
 *
 *
 *
 * @property { function() } getNoteListRef
 * @property { function() } getNoteList
 * @property { function(noteId) } getNote
 * @property { function(noteItem) } updateNote
 * @property { function(noteItem) } createNote
 * @property { function(noteItem) } deleteNote
 *
 *
 * @property { function() } getTagsContainerListRef
 * @property { function() } getTagsContainerList
 * @property { function(tagsContainerId) } getTagsContainer
 * @property { function(tagsContainer) } updateTagsContainer
 * @property { function(tagsContainer) } setTagsContainer
 * @property { function(tagsContainer) } createTagsContainer
 * @property { function(tagsContainer) } deleteTagsContainer
 *
 *
 * @property { function() } getDriveFileList
 * @property { function() } getDriveFileListPager
 * @property { function() } getDriveFileListPath
 * @property { function(qwoachFileId) } getDriveFile
 * @property { function(qwoachFile) } updateDriveFile
 * @property { function(qwoachFile) } setDriveFile
 * @property { function(qwoachFile) } createDriveFile
 * @property { function(qwoachFile) } deleteDriveFile
 *
 * @property { function() } getLinkItemListPager
 * @property { function(linkItemId) } getLinkItem
 * @property { function(linkItem) } updateLinkItem
 * @property { function(linkItem) } setLinkItem
 * @property { function(linkItem) } createLinkItem
 * @property { function(linkItem) } deleteLinkItem
 *
 * @property { function(formItemId) } getFormItem
 * @property { function(formItemId) } getFormItemRef
 * @property { function(formItemId) } getFormItemListRef
 * @property { function(formItemId) } getFormItemListPager
 * @property { function(formItem) } updateFormItem
 * @property { function(formItem) } setFormItem
 * @property { function(formItem) } createFormItem
 * @property { function(formItem) } deleteFormItem
 *
 * @property { function(formItemId) } getFormAnswerItem
 * @property { function(formItemId) } getFormAnswerItemListRef
 * @property { function(formItem) } updateFormAnswerItem
 * @property { function(formItem) } setFormAnswerItem
 * @property { function(formItem) } createFormAnswerItem
 * @property { function(formItem) } deleteFormAnswerItem
 *
 *
 * @property { function(workflowItemId) } getWorkflowItem
 * @property { function(workflowItemId) } getWorkflowItemRef
 * @property { function(workflowItemId) } getWorkflowItemListRef
 * @property { function(workflowItem) } updateWorkflowItem
 * @property { function(workflowItem) } setWorkflowItem
 * @property { function(workflowItem) } createWorkflowItem
 * @property { function(workflowItem) } deleteWorkflowItem
 *
 * @property { function(workflowEntryItemId) } getWorkflowEntryItem
 * @property { function(workflowEntryItemId) } getWorkflowEntryItemListPager
 * @property { function(workflowEntryItem) } updateWorkflowEntryItem
 * @property { function(workflowEntryItem) } setWorkflowEntryItem
 * @property { function(workflowEntryItem) } createWorkflowEntryItem
 * @property { function(workflowEntryItem) } deleteWorkflowEntryItem
 *
 *
 *
 * @property { function(workflowStateItemId) } getWorkflowStateItem
 * @property { function(workflowStateItemId) } getWorkflowStateItemRef
 * @property { function(workflowStateItemId) } getWorkflowStateItemListRef
 * @property { function(workflowStateItemId) } getWorkflowStateItemListPager
 * @property { function(workflowStateItem) } updateWorkflowStateItem
 * @property { function(workflowStateItem) } setWorkflowStateItem
 * @property { function(workflowStateItem) } createWorkflowStateItem
 * @property { function(workflowStateItem) } deleteWorkflowStateItem
 *
 *
 *
 *
 * @property { function(qwoachFile) } getDriveStats
 * @property { function(qwoachFile) } setDriveStats
 *
 * @property { function() } getDocSignTemplateListRef
 * @property { function() } getDocSignTemplateList
 * @property { function() } getDocSignTemplateListPager
 * @property { function(docSignTemplateId) } getDocSignTemplate
 * @property { function(docSignTemplate) } updateDocSignTemplate
 * @property { function(docSignTemplate) } setDocSignTemplate
 * @property { function(docSignTemplate) } createDocSignTemplate
 * @property { function(docSignTemplate) } deleteDocSignTemplate
 *
 * @property { function() } getContractRef
 * @property { function() } getContractList
 * @property { function() } getContractListPager
 * @property { function(contractId) } getContract
 * @property { function(contract) } updateContract
 * @property { function(contract) } setContract
 * @property { function(contract) } createContract
 * @property { function(contract) } deleteContract
 *
 *
 *
 * @property { function() } getSignatureItemListRef
 * @property { function() } getSignatureItemList
 * @property { function(signatureItemId) } getSignatureItem
 * @property { function(signatureItem) } updateSignatureItem
 * @property { function(signatureItem) } setSignatureItem
 * @property { function(signatureItem) } createSignatureItem
 * @property { function(signatureItem) } deleteSignatureItem
 *
 *
 * @property { function(billingDataId) } getBillingData
 * @property { function(billingDataId) } getBillingDataListRef
 * @property { function(billingData) } updateBillingData
 * @property { function(billingData) } setBillingData
 * @property { function(billingData) } createBillingData
 * @property { function(billingData) } deleteBillingData
 *
 *
 * @property { function(paymentIntegrationsId) } getPaymentIntegrations
 * @property { function(paymentIntegrations) } updatePaymentIntegrations
 * @property { function(paymentIntegrations) } setPaymentIntegrations
 * @property { function(paymentIntegrations) } createPaymentIntegrations
 * @property { function(paymentIntegrations) } deletePaymentIntegrations
 *
 * @property { function(eventTypeId) } getEventTypeList
 * @property { function(eventTypeId) } getEventTypeListPager
 * @property { function(eventTypeId) } getEventTypeListRef
 * @property { function(eventTypeId) } getEventType
 * @property { function(eventType) } updateEventType
 * @property { function(eventType) } setEventType
 * @property { function(eventType) } createEventType
 * @property { function(eventType) } deleteEventType
 *
 *
 * @property { function(scheduledEventItemId) } getScheduledEventItemListPager
 * @property { function(scheduledEventItemId) } getScheduledEventItemListRef
 * @property { function(scheduledEventItemId) } getScheduledEventItemList
 * @property { function(scheduledEventItemId) } getScheduledEventItemListPath
 * @property { function(scheduledEventItemId) } getScheduledEventItem
 *
 // WARNING!! event CRUD must be performed via api since it also syncs reminders
 * @property { function(scheduledEventItem) } updateScheduledEventItem
 // * @property { function(scheduledEventItem) } setScheduledEventItem
 // * @property { function(scheduledEventItem) } createScheduledEventItem
 // * @property { function(scheduledEventItem) } deleteScheduledEventItem
 *
 *
 *
 *
 *
 * @property { function() } getInvoiceGroupItemListRef
 * @property { function(invoiceGroupId) } getInvoiceGroupItem
 * @property { function(invoiceGroup) } updateInvoiceGroupItem
 * @property { function(invoiceGroup) } setInvoiceGroupItem
 * @property { function(invoiceGroup) } createInvoiceGroupItem
 * @property { function(invoiceGroup) } deleteInvoiceGroupItem
 *
 * @property { function() } getInvoiceItemListRef
 * @property { function() } getInvoiceItemListPager
 * @property { function(invoiceGroupId) } getInvoiceItem
 * @property { function(invoiceGroup) } updateInvoiceItem
 * @property { function(invoiceGroup) } setInvoiceItem
 * @property { function(invoiceGroup) } createInvoiceItem
 * @property { function(invoiceGroup) } deleteInvoiceItem
 *
 * @property { function() } getGenogramItemListRef
 * @property { function() } getGenogramItemListPager
 * @property { function(genogramItemId) } getGenogramItem
 * @property { function(genogramItem) } updateGenogramItem
 * @property { function(genogramItem) } setGenogramItem
 * @property { function(genogramItem) } createGenogramItem
 * @property { function(genogramItem) } deleteGenogramItem
 *
 *
 * @property { function() } getIFSPartsItemListRef
 * @property { function() } getIFSPartsItemListPager
 * @property { function(ifsPartsItemId) } getIFSPartsItem
 * @property { function(ifsPartsItem) } updateIFSPartsItem
 * @property { function(ifsPartsItem) } setIFSPartsItem
 * @property { function(ifsPartsItem) } createIFSPartsItem
 * @property { function(ifsPartsItem) } deleteIFSPartsItem
 *
 *
 * @property { function() } getWheelOfLifeTemplateListRef
 * @property { function() } getWheelOfLifeTemplateListPager
 * @property { function(wheelId) } getWheelOfLifeTemplate
 * @property { function(wheelId) } updateWheelOfLifeTemplate
 * @property { function(wheelId) } setWheelOfLifeTemplate
 * @property { function(wheelId) } createWheelOfLifeTemplate
 * @property { function(wheelId) } deleteWheelOfLifeTemplate
 *
 * @property { function() } getWheelOfLifeResponseList
 * @property { function() } getWheelOfLifeResponseListRef
 * @property { function() } getWheelOfLifeResponseListPager
 * @property { function(wheelId) } getWheelOfLifeResponse
 * @property { function(wheelId) } updateWheelOfLifeResponse
 * @property { function(wheelId) } setWheelOfLifeResponse
 * @property { function(wheelId) } createWheelOfLifeResponse
 * @property { function(wheelId) } deleteWheelOfLifeResponse
 *
 *
 *
 * @property { function() } getCompanyIndex
 * @property { function() } updateCompanyIndex
 * @property { function() } setCompanyIndex
 * @property { function() } createCompanyIndex
 * @property { function() } deleteCompanyIndex
 *
 * @property { function() } getTimelineItemListRef
 * @property { function() } getTimelineItemListPager
 * @property { function(timelineItemId) } getTimelineItem
 * @property { function(timelineItem) } updateTimelineItem
 * @property { function(timelineItem) } setTimelineItem
 * @property { function(timelineItem) } createTimelineItem
 * @property { function(timelineItem) } deleteTimelineItem
 *
 *
 *
 * @property { function() } getTimeBasedItemListRef
 * @property { function(timeBasedItemId) } getTimeBasedItem
 * @property { function(timeBasedItem) } updateTimeBasedItem
 * @property { function(timeBasedItem) } setTimeBasedItem
 * @property { function(timeBasedItem) } createTimeBasedItem
 * @property { function(timeBasedItem) } deleteTimeBasedItem
 *
 *
 *
 * @property { function(userId, assessmentId) }   getAssessment
 * @property { function(userId, assessmentItem) } createAssessment
 * @property { function(userId, assessmentItem) } updateAssessment
 * @property { function(userId, assessmentItem) } deleteAssessment
 *
 * @property { function(userId, assessmentId) }   getAssessmentPublic
 * @property { function(userId, assessmentItem) } createAssessmentPublic
 * @property { function(userId, assessmentItem) } updateAssessmentPublic
 * @property { function(userId, assessmentItem) } deleteAssessmentPublic
 *
 *
 *
 * @property { function({scheduledEvent}) } apiPostEventCreate
 * @property { function({scheduledEvent}) } apiPostEventCancel
 * @property { function({scheduledEvent}) } apiPostEventSyncDelete
 * @property { function({scheduledEvent}) } apiPostEventCancelNotify
 * @property { function({eventTypeUserId}) } apiGetSchedulerFindUserByEventTypeUserId
 *
 * @property { function({scheduledEvent}) } apiPostScheduledEventCreate
 * @property { function({[scheduledEvent]}) } apiPostScheduledEventCreateMultiple
 * @property { function({[scheduledEvent]}) } apiPostScheduledEventUpdateMultiple
 * @property { function({scheduledEvent}) } apiPostScheduledEventUpdate
 * @property { function({eventTypeUserId}) } apiPostScheduledEventDelete
 * @property { function({eventTypeUserId}) } apiPostScheduledEventDeleteMultiple
 *
 *
 *
 *
 *
 * @property { function({eventTypeUserId, eventTypeId}) } apiGetEventTypePublic
 * @property { function({userId, eventTypeId}) } apiGetEventTypeByIdPublic
 *
 * @property { function({scheduledEventId}) } apiGetScheduledEventPublic
 * @property { function({scheduledEventId}) } apiGetSchedulerPublicGetEventById
 *
 *
 * @property { function(programInvite) } apiPostSendInvite
 * @property { function(programInvite) } apiPostAcceptProgramInvite
 *
 * @property { function(assessmentRequest) } apiGetAssessmentPublic
 * @property { function(assessmentRequest) } apiPostAssessmentUpdateAssignee
 *
 * @property { function(signatureItem) } apiPostCreateSignatureItem
 * @property { function(signatureItem) } apiPostDeleteSignatureItem
 *
 * @property { function(request) } apiPostGCalendarAuthStart
 * @property { function(request) } apiPostOutlook365AuthStart
 *
 * @property { function(request) } apiGetCalendarEventRecurringInstances
 * @property { function(request) } apiPostCalendarEventUpdate
 * @property { function(request) } apiPostCalendarEventUpdateFuture
 * @property { function(request) } apiPostCalendarEventDelete
 * @property { function(request) } apiPostCalendarEventDeleteFuture
 *
 * @property { function(request) } apiPostCopyEventType
 * @property { function(request) } apiPostDeleteEventType
 *
 *
 *
 *
 *
 *
 * @property { function(request) } apiPostPayPalPaymentLink
 * @property { function(request) } apiPostPayPalIntegrationValidate
 * @property { function(request) } apiPostPayPalCreateOrder
 * @property { function(request) } apiPostPayPalApproveOrder
 *
 * @property { function(request) } apiGetStripeApiKey
 *
 *
 * @property { function(request) } apiPostLogError
 *
 *
 */


export default class QwoachDataManager extends FirestoreDB {

	ownerId;
	currentUserId;

	auth
	store

	constructor(firebase, store, analytics) {
		//WARNING !! it's very important to pass local 'plainToClass' function so the library can correctly transform nested objects
		//unless before class-transform fixes a problem where it can't transform nested objects when used from npm package
		// github issue: https://github.com/typestack/class-transformer/issues/313
		super(firebase, fbPathsConfig, getApiDomain(), plainToClass, classToPlain, {excludePrefixes: ["_"]}, {isDebugEnabled: isDevEnv})
		this.auth = firebase.auth()
		this.store = store
		this.analyticManager = analytics
		console.log("QwoachDataManager = ", this)
	}

	onMethodCalled(methodName, parsedPath, data, clazz, options){
		console.log("QwoachDataManager.onMethodCalled: ", {methodName, parsedPath, data, clazz, options})
		if(this.analyticManager){
			this.analyticManager.trackEvent("Data manager called", {methodName, parsedPath})
		}
	}

	getFunnelItem() {
		return this.getFunnelListRef()
			.limit(1)
			.get()
			.then(res => this.resArrayToData(res, OldFunnelItem))
			.then(funnel => {
				console.log("getFunnelItem = ", funnel)
				return funnel;
			})
	}

	async getFunnelItemMain() {
		let funnelMain = await this.getFunnelMain()
		if(!funnelMain){
			funnelMain = new OldFunnelItem()
			funnelMain.id = null
			funnelMain = await this.setFunnelMain(funnelMain)
		}
		return funnelMain
	}

	async getFunnelEntriesPager2(funnelId, columnId, limit = 10) {
		let ref = await this.getFunnelEntryListRef(funnelId)
		console.log("getFunnelEntriesPager2: ref = ", ref)
		let dataFilter = new DataFilter(limit)
			.addFilterOption("columnId", "==", columnId)
			.addSortOption("index", SortDirection.DESC)
		return this.fetchListPager(ref, dataFilter, FunnelEntryItem)
	}


	async getFunnelEntryByClientEmail(funnelId, clientEmail) {
		let res = await this.getFunnelEntryListRef(funnelId)
			.where("clientProfile.email", "==", clientEmail)
			.limit(1)
			.get()
		return this.resArrayToData(res, FunnelEntryItem)
	}

	async getTagsContainerItem() {
		let res = await this.getTagsContainerListRef()
			.limit(1)
			.get()
		console.log("getTagsContainerItem: res = ", res)
		let tagsContainer = this.resArrayToData(res, TagsContainer)
		console.log("getTagsContainerItem = ", tagsContainer)
		return tagsContainer || new TagsContainer();
	}
	async getGenogramByClientId(clientId) {
		let dataFilter = new DataFilter(1)
			.where("clientId", "==", clientId)

		let pager = await this.getGenogramItemListPager(dataFilter)
		let data = await pager.next()
		return data[0]
	}
	async getIFSPartsItemByClientId(clientId) {
		let dataFilter = new DataFilter(1)
			.where("clientId", "==", clientId)

		let pager = await this.getIFSPartsItemListPager(dataFilter)
		let data = await pager.next()
		return data[0]
	}

	async getTimelineItemByClientId(clientId) {
		let dataFilter = new DataFilter(1)
			.where("clientId", "==", clientId)

		let pager = await this.getTimelineItemListPager(dataFilter)
		let data = await pager.next()
		return data[0] || new TimelineItem(clientId)
	}

	async getNoteListByOwnerId(ownerId) {
		let res = await this.getNoteListRef()
			.where('ownerIds', 'array-contains', ownerId)
			.orderBy('createdAt', 'desc')
			.get()
		return this.resArrayToDataArray(res, NoteItem)
	}

	async getPurchasedCoachingPackageListByClientId(clientId){
		let dataFilter = new DataFilter(1000)
			.where("sharedWithIds", "array-contains", clientId)
			.orderByDesc("createdAt")
		let pager = await this.getPurchasedCoachingPackageListPager(dataFilter)
		let packages = await pager.next()
		if(packages.length === 0){
			packages = await this.getPurchasedCoachingPackageListByClientIdOld(clientId)
		}
		return packages
	}
	async getPurchasedCoachingPackageListByClientIdOld(clientId){
		//TODO legacy approach. Proper way is to perform migration from clientId to sharedWithIds
		let dataFilter = new DataFilter(1000)
			.where("clientId", "==", clientId)
			.orderByDesc("createdAt")
		let pager = await this.getPurchasedCoachingPackageListPager(dataFilter)
		return pager.next()
	}


	async schedulerIsRootUrlFree(userTypeId) {
		let user = await this.apiGetSchedulerFindUserByEventTypeUserId({eventTypeUserId: userTypeId})
		return user == null
	}


	// getClientProfileList(filter = new FiltersItem()){
	// 	return filter.apply(this.getClientProfileListRef())
	// 		.orderBy('createdAt', 'desc')
	// 		.get()
	// 		.then(res => this.resArrayToDataArray(res, ClientProfile))
	// }

	getPaymentIntegrationsMain() {
		return this.getPaymentIntegrations()
			.then(res => res || new PaymentIntegrations())
	}


	async getInvoiceGroupsByProgramId(programId) {
		let res = await this.getInvoiceGroupItemListRef()
			.where(`programIdIndex.${programId}`, '==', true)
			.get()
		return this.resArrayToDataArray(res, InvoiceGroup)
	}

	async findClientProfileByEmail(email) {
		let res = await this.getClientProfileListRef()
			.where('email', '==', email.trim().toLowerCase())
			.limit(1)
			.get()
		return this.resArrayToData(res, ClientProfile)
	}

	async getClientListByProgramId(programId) {
		let res = await this.getClientProfileListRef()
			.where('programIds', 'array-contains', programId)
			.get()
		return  this.resArrayToDataArray(res, ClientProfile)
	}

	async getClientList(dataFilter = new DataFilter()) {
		let ref = this.getClientProfileListRef()
		// ref = ref.where("isDeleted", "!=", false)
		ref = dataFilter.applyFilters(ref)
		let res = await ref.get()
		return  this.resArrayToDataArray(res, ClientProfile)
	}



	async getBillingDataByProgramId(programId) {
		let res = await this.getBillingDataListRef()
			.where('programId', '==', programId)
			.limit(1)
			.get()
		return this.resArrayToData(res, BillingData)
	}


	getTimeBasedItemsByParentId(parentId, parentSecondaryId = "") {
		console.log('getTimeBasedItemsByParentId: parentId = ', parentId, ', parentSecondaryId = ', parentSecondaryId)
		return this.getTimeBasedItemListRef()
			.where(`parentId`, '==', parentId)
			.where(`parentSecondaryId`, '==', parentSecondaryId)
			.get()
			.then(res => this.resArrayToDataArray(res, TimeBasedItem))
	}

	// async getUserSubscriptionOrDefault(userId){
	// 	return await this.getUserSubscription(userId) || new UserSubscription()
	// }
	async getCompanyIndexOrDefault(){
		return await this.getCompanyIndex() || new CompanyIndexItem()
	}

	async listTemplateEventTypes() {
		return await this.listSessionTypes(EventTypeCategory.TEMPLATE)
	}

	async listSessionTypes(category = EventTypeCategory.SCHEDULER) {
		let res = await this.getEventTypeListRef()
			.orderBy('createdAt', 'desc')
			.where('category', '==', category)
			.get()
		res = this.resArrayToDataArray(res, EventType)//, this.getEventTypeListPath())
		res = this.applyAvailability(res)
		return res
	}
	async getSessionTypesPager(dataFilter) {
		if(!dataFilter){
			dataFilter = new DataFilter()
				.setLimit(6)
				.orderByDesc("createdAt")
				.where("category", "==", EventTypeCategory.SCHEDULER)
		}

		let pager = await super.getEventTypeListPager(dataFilter)
		console.log("getSessionTypesPager: pager = ", pager)
		pager.resArrayToDataArrayFn = (res) => {
			res = this.resArrayToDataArray(res, EventType)
			res = this.applyAvailability(res)
			return res
		}
		return pager
	}

	// async getUsageStatsOrDefault(userId){
	// 	return await this.getUsageStats(userId) || new UsageStats()
	// }



	async findSessionTypeByOwnerId(ownerId, category = EventTypeCategory.PROGRAM) {
		let res = await this.getEventTypeListRef()
			.orderBy('createdAt', 'asc')
			.where('ownerIds', 'array-contains', `${ownerId}`)
			.where('category', '==', `${category}`)
			.get()
		res = this.resArrayToDataArray(res, EventType)//, this.getEventTypeListPath())
		res = this.applyAvailability(res)
		return res
	}

	async findScheduledSessionsByOwnerId(ownerId) {
		let res = await this.getScheduledEventItemListRef()
			.orderBy('createdAt', 'asc')
			.where('ownerIds', 'array-contains', `${ownerId}`)
			.get()
		res = this.resArrayToDataArray(res, ScheduledEvent)//, this.getScheduledEventItemListPath())
		return res
	}

	applyAvailability(sessionTypeList) {
		let globalAvailability = this.getCoachProfileStore().schedulerSettings.availabilitySettings
		sessionTypeList = sessionTypeList.map(eventType => {
			if (!eventType.isAvailabilityLocal) {
				eventType.availabilitySettings = globalAvailability;
			}
			return eventType;
		})
		return sessionTypeList
	}


	async schedulerListConfirmedEvents(startTime, endTime) {
		startTime = dayjs(startTime || new Date());
		endTime = endTime || dayjs(new Date()).add(1, 'year')
		endTime = dayjs(endTime)
		let res = await this.getScheduledEventItemListRef()
			.orderBy('startTime', 'asc')
			.where('startTime', '>=', startTime.valueOf())
			.where('startTime', '<=', endTime.valueOf())
			.where('confirmationStatus', 'in', [SessionConfirmationStatus.CONFIRMED])
			.where('coachId', '==', this.ownerId)
			.get()

		res = this.resArrayToDataArray(res, ScheduledEvent)//, this.getScheduledEventItemListPath())
		return res
	}

	async getFilesInDriveFolderPager(parentFolderId = null, limit = 10) {
		console.log("getFilesInDriveFolder: parentFolderId = ", parentFolderId)
		let filter = new DataFilter()
			.setLimit(limit)
			.orderBy('isFolder', 'desc')
			.orderBy('name_casefolded', 'asc')
			// .where('isDeleted', '==', false)
			.where('parentId', '==', parentFolderId)
		return this.getDriveFileListPager(filter)
	}

	async getFoldersInDriveFolderPager(parentFolderId = null, limit = 10) {
		console.log("getFilesInDriveFolder: parentFolderId = ", parentFolderId)
		let filter = new DataFilter()
			.setLimit(limit)
			.orderBy('isFolder', 'desc')
			.orderBy('name_casefolded', 'asc')
			// .where('isDeleted', '==', false)
			.where('parentId', '==', parentFolderId)
			.where('isFolder', '==', true)

		return this.getDriveFileListPager(filter)
	}

	async getClientFilesPager(clientId, parentFolderId = null, limit = 10) {
		console.log("getFilesInDriveFolder: parentFolderId = ", parentFolderId)
		let dataFilter = new DataFilter()
			.setLimit(limit)
			.orderBy('isFolder', 'desc')
			.orderBy('name_casefolded', 'asc')
			.where('sharedWithIds', 'array-contains', clientId)
			// .where('isDeleted', '==', false)
			.where('parentId', '==', parentFolderId)
		return this.getDriveFileListPager(dataFilter)
	}
	async getFilesSharedPager(sharedWithArrayIds, folderId, limit = 10) {
		console.log("getClientFilesPagerNoFolder: ", arguments)
		let dataFilter = new DataFilter()
			.setLimit(limit)
			.orderBy('isFolder', 'desc')
			.orderBy('name_casefolded', 'asc')
			.whereIf("parentId", "==", folderId, folderId != null)
			.where('sharedWithIds', 'array-contains-any', sharedWithArrayIds)
			// .where('isDeleted', '==', false)
		return this.getDriveFileListPager(dataFilter)
	}


	async getFilesInDriveTrashPager(limit = 10) {
		console.log("pagerGetFilesInDriveTrash")
		let dataFilter = new DataFilter()
			.setLimit(limit)
			.orderBy('updatedAt', 'desc')
			.where('isDeleted', '==', true)
		return this.getDriveFileListPager(dataFilter)

	}

	async getFilesInDriveRecentPager(limit = 10) {
		console.log("pagerGetFilesInDriveTrash")
		let dataFilter = new DataFilter()
			.setLimit(limit)
			.orderBy('updatedAt', 'desc')
			.where('isDeleted', '==', false)
		return this.getDriveFileListPager(dataFilter)

	}

	async getProgramInviteByProgramId(programId) {
		let res = await this.getProgramInviteListRef()
			.where('programId', '==', programId)
			.limit(1)
			.get()
		return this.resArrayToData(res, ProgramInvite)
	}

	getCoachingProcessesPager(limit = 10) {
		let ref = this.getProgramListRef()
		let dataFilter = new DataFilter(limit)
			.orderBy('createdAt', 'desc')
			.where('programCategory', '==', ProgramCategoryEnum.PROCESS)
		return this.fetchListPager(ref, dataFilter, CoachingProgram)
	}

	getProgramTemplatesPager(limit = 10) {
		let ref = this.getProgramListRef()
		let dataFilter = new DataFilter(limit)
			.orderBy('createdAt', 'desc')
			.where('programCategory', '==', ProgramCategoryEnum.TEMPLATE)
		return this.fetchListPager(ref, dataFilter, CoachingProgram)
	}


	async getOverdueTasksCoachJournal(clientId, options) {
		let dueDateNow = new Date().getTime()
		return await this.getTasksByDueDate(clientId, DisplayDestinationEnum.COACH_JOURNAL, 1, dueDateNow, options)
	}

	async getOverdueTasksClientJournalForCoach(clientId, options) {
		let dueDateNow = new Date().getTime()
		return await this.getTasksByDueDate(clientId, DisplayDestinationEnum.CLIENT_JOURNAL_FOR_COACH, 1, dueDateNow, options)
	}

	async getUpcomingTasksCoachJournal(clientId, dueDateHigh, options) {
		let dueDateNow = new Date().getTime()
		return await this.getTasksByDueDate(clientId, DisplayDestinationEnum.COACH_JOURNAL, dueDateNow, dueDateHigh, options)
	}

	async getUpcomingTasksClientJournalForCoach(clientId, dueDateHigh, options) {
		let dueDateNow = new Date().getTime()
		return await this.getTasksByDueDate(clientId, DisplayDestinationEnum.CLIENT_JOURNAL_FOR_COACH, dueDateNow, dueDateHigh, options)
	}

	async getUpcomingTasksClientJournalForClient(clientId, dueDateHigh, options) {
		let dueDateNow = new Date().getTime()
		return await this.getTasksByDueDate(clientId, DisplayDestinationEnum.CLIENT_JOURNAL_FOR_CLIENT, dueDateNow, dueDateHigh, options)
	}

	async getTasksByDueDate(clientId, displayDestination, dueDateLow, dueDateHigh, options){
		if(!Array.isArray(displayDestination)){
			displayDestination = [displayDestination]
		}
		let dataFilter = new DataFilter()
			.where('displayDestination', 'object-contains-any', displayDestination)
			.where('clientId', '==', clientId)
			.where('isCompleted', '==', false)
			.where('dueDateTs', '>', dueDateLow)
			.where('dueDateTs', '<', dueDateHigh)
			.setLimit(10)
			.orderBy('dueDateTs', 'asc')
		return this.getJournalEntryListPager(dataFilter, options)
	}


	async getCoachJournalEntryListPager(clientId, options) {
		return this.getJournalEntryListPagerInternal(clientId, DisplayDestinationEnum.COACH_JOURNAL, 10, undefined, options)
	}

	async getClientJournalForCoachEntryListPager(clientId, limit = 10, filterType, options) {
		return this.getJournalEntryListPagerInternal(clientId, DisplayDestinationEnum.CLIENT_JOURNAL_FOR_COACH, limit, filterType, options)
	}

	async getClientJournalForClientEntryListPager(clientId, limit = 10, filterType, options) {
		return this.getJournalEntryListPagerInternal(clientId, DisplayDestinationEnum.CLIENT_JOURNAL_FOR_CLIENT, limit, filterType, options)
	}


	async getJournalEntryListPagerInternal(clientId, displayDestination, limit = 10, filterType, options = undefined) {
		if(!Array.isArray(displayDestination)){
			displayDestination = [displayDestination]
		}
		if(!Array.isArray(clientId)){
			clientId = [clientId]
		}
		let dataFilter = new DataFilter()
				.where('displayDestination', 'object-contains-any', displayDestination)
				.where('clientId', '==', clientId[0])
				.whereIf("type", "==", filterType)
				.orderBy('createdAt', 'desc')

		return this.getJournalEntryListPager(dataFilter, options)
	}

	// getGroupAndClientIdsObject(idObject){
	// 	let groupIds
	// 	let clientId
	// 	if(isObject(idObject)){
	// 		groupIds = idObject.groupIds
	// 		clientId = idObject.clientId
	// 	}
	// 	if(!Array.isArray(groupIds)){
	// 		groupIds = [groupIds]
	// 	}
	//
	// 	return {groupIds, clientId}
	// }

	// getGroupAndClientIdsArray(idObject){
	// 	let {groupIds, clientId} = this.getGroupAndClientIdsArray()
	//
	// 	return
	// }

	async getDashboardJournalEntryListPager(limit = 5, dataFilter2) {
		let displayDestination = [
			DisplayDestinationEnum.CLIENT_JOURNAL_FOR_COACH,
			DisplayDestinationEnum.COACH_JOURNAL,
		]

		let last100Days = dayjs().add(-100, "day").valueOf()

		let res
		// if(isDevEnv){
		// 	res = this.getJournalEntryListRef()
		// 		.where('type', '==', JournalEntryType.LEGAL_DOC)
		// 		.limit(limit)
		// 		.orderBy('createdAt', 'desc')
		// } else {
			let dataFilter = new DataFilter()
				.merge(dataFilter2)
				.where('displayDestination', 'object-contains-any', displayDestination)
				.where('isCompleted', '==', false)
				.where('isClientEnabled', '==', true)
				.where('createdAt', '>', last100Days)
				.setLimit(limit)
				.orderBy('createdAt', 'asc')
		// }

		return this.getJournalEntryListPager(dataFilter)
	}

	async getDashboardJournalEntryListByRange(startMs, endMs) {

		let displayDestination = [
			DisplayDestinationEnum.CLIENT_JOURNAL_FOR_COACH,
			DisplayDestinationEnum.COACH_JOURNAL,
		]

		let dataFilter = new DataFilter(1000)
			.where('displayDestination', 'object-contains-any', displayDestination)
			.where('isCompleted', '==', false)
			.where('dueDateTs', '>=', startMs)
			.where('dueDateTs', '<=', endMs)
			.orderBy('dueDateTs', 'asc')

		let pager = await this.getJournalEntryListPager(dataFilter)
		return pager.loadAll()
	}


	async hasProgramTemplates() {
		let res = await this.getProgramListRef()
			.orderBy('createdAt', 'desc')
			.where('programCategory', '==', ProgramCategoryEnum.TEMPLATE)
			.limit(1)
			.get()
		return this.resArrayToData(res, CoachingProgram) != null
	}

	getFormAnswerListPager(formId, limit = 10) {
		let ref = this.getFormAnswerItemListRef()
		let dataFilter = new DataFilter(limit)
			.where("formId", "==", formId)
			.orderBy('createdAt', 'desc')
		return this.fetchListPager(ref, dataFilter, FormAnswerItem)
	}

	async getProspectingWorkflowItemOrCreate(){
		let workflowItem = await this.getWorkflowItem(WORKFLOW_ID_PROSPECTING)
		if(!workflowItem){
			workflowItem = await this.apiPostWorkflowItemProspectCreate({
				ownerId: this.ownerId,
			})
		}
		return workflowItem
	}

	async deleteFormItemAndFormAnswers(formItem) {
		let answerPager = this.getFormAnswerListPager(formItem.id, 100)
		let answerData = await answerPager.next()
		while (answerData.length > 0) {
			console.log("deleteFormItemWithAnswers: answerData = ", answerData)
			await this.batchDeleteFormAnswerItems(answerData)
			answerData = await answerPager.next()
		}
		await this.deleteFormItem(formItem)
	}

	getCurrentUser() {
		return this.store.state.store.currentUser;
	}

	getCoachProfileStore() {
		return this.store.state.store.coachProfile;
	}

	getCurrentUserProfileStore() {
		return this.store.state.store.userProfile;
	}


	// findScheduledSessionsByOwnerId(ownerId){
	// 	return this.getScheduledEventItemListRef()
	// 		.orderBy('createdAt', 'asc')
	// 		.where('ownerIds', 'array-contains', `${ownerId}`)
	// 		.get()
	// 		.then(res => {
	// 			let list = res.docs.map(doc => plainToClass(ScheduledEvent, doc.data()));
	// 			list = this.applyAvailability(list)
	// 			console.log("findScheduledSessionsByOwnerId: list = ", list)
	// 			return list;
	// 		})
	// }

	async logError(error, info) {
		// console.trace("logError")
		try {
			console.log("logError: error = ", error, ", info = ", info)
			if(!error){
				return
			}

			error = error || {}
			let stack = []
			try{
				stack = ErrorStackParser.parse(error)
			}catch (e) {
				console.warn(e)
			}

			console.log(stack)
			let currentUser = this.getCurrentUser() || {}
			let data = {
				stack: stack,
				stackOriginal: error.stack,
				name: error.name,
				message: error.message,
				error: error,
				info: info,
				userEmail: currentUser.email,
				userId: currentUser.uid,
			}
			let str = JSON.stringify(error.stack)
			console.log("error as string = ", str, ", Object.getOwnPropertyNames(error) = ", Object.keys(error))
			console.log("error data = ", data)
			if (isProdEnv) {
				await this.apiPostLogError(data)
			} else {
				console.log("Ignoring error logging for current environment: ", env)
			}
			return data

		} catch (error) {
			console.error(error)
		}
	}


	async sendPasswordResetEmail(email){
		return await this.auth.sendPasswordResetEmail(email)
	}
	//
	// async signInWithEmailAndPassword(email, password){
	// 	let response = await this.auth.signInWithEmailAndPassword(email, password)
	// 	this.store.commit('setCurrentUser', response.user)
	// 	return this.getCurrentUserProfile();
	// }
	//
	// async isUserExist(email: string) {
	// 	try{
	// 		let response = await this.auth.fetchSignInMethodsForEmail(email)
	// 		console.log("isUserExists: response = ", response)
	// 		return (response || []).length > 0;
	// 	} catch (error) {
	// 		console.error("isUserExists: error = ", error)
	// 	}
	// 	return false;
	// }
	//
	//
	//
	//
	// async createUserWithEmailAndPassword(password, userProfile){
	// 	console.log("createUserWithEmailAndPassword: userProfile = ", userProfile)
	// 	let response = await this.auth.createUserWithEmailAndPassword(userProfile.email, password)
	// 	let user = response.user
	// 	userProfile.id = user.uid
	// 	this.store.commit('setCurrentUser', user)
	// 	console.log("createUserWithEmailAndPassword: userProfile = ", userProfile)
	// 			return this.createUserProfile(userProfile);
	// 		}).then(() => {
	// 			return this.getCurrentUserProfile();
	// 		});
	// }

	async isUserExist(email) {
		let res = false;
		try {
			let response = await this.auth.fetchSignInMethodsForEmail(email)
			res = response && response.length;
		} catch (error) {
			console.warn("isUserExists: error = ", error)
		}
		return res
	}

	filterInvoices(limit: number = 20) {
		let ref = this.getInvoiceGroupItemListRef()
		let dataFilter = new DataFilter(limit)
			.orderBy("createdAt", "desc")
		return this.fetchListPager(ref, dataFilter, InvoiceItem)
	}



	fetchListPager(ref, dataFilter, clazz, options = {}){
		console.log("QwoachDataManager.fetchListPager: ref.path = ", ref.path, ", dataFilter = ", dataFilter, ", clazz = ", clazz, ", options = ", options)
		if(this.portalManager){
			dataFilter = this.portalManager.decorateDataFilterForNonOwner(dataFilter, options)
		}
		return super.fetchListPager(ref, dataFilter, clazz, options)
	}

	generateUid(){
		return this.generateId(28)
	}

}
